import React, { useState } from 'react';

const SuccessStories = () => {
  const [activeCase, setActiveCase] = useState(0);
  const [hoveredMetric, setHoveredMetric] = useState(null);

  const caseStudies = [
    {
      id: 1,
      category: "E-Commerce",
      title: "Retail Brand Growth",
      client: "Fashion Forward Co.",
      duration: "6 months",
      description: "In just 6 months, we helped a retail fashion brand boost its online sales by 300% through a combination of SEO, influencer marketing, and Google Ads.",
      challenge: "Low online visibility and declining sales in competitive fashion market",
      solution: "Multi-channel digital strategy focusing on SEO optimization, influencer partnerships, and targeted Google Ads campaigns",
      results: [
        { metric: "Sales Increase", value: "300%", icon: "📈", color: "#10b981" },
        { metric: "Organic Traffic", value: "450%", icon: "🚀", color: "#3b82f6" },
        { metric: "Conversion Rate", value: "85%", icon: "⚡", color: "#f59e0b" },
        { metric: "ROAS", value: "6.2x", icon: "💰", color: "#8b5cf6" }
      ],
      tags: ["SEO", "Google Ads", "Influencer Marketing"],
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      bgColor: "#667eea",
      textColor: "#667eea"
    },
    {
      id: 2,
      category: "B2B SaaS",
      title: "SaaS Lead Generation",
      client: "TechFlow Solutions",
      duration: "3 months",
      description: "Our targeted LinkedIn ad campaign generated 500+ high-quality leads for a B2B SaaS company in under 3 months.",
      challenge: "Difficulty reaching decision makers and generating qualified leads in competitive B2B space",
      solution: "Strategic LinkedIn advertising with precise targeting, compelling ad creatives, and optimized landing pages",
      results: [
        { metric: "Quality Leads", value: "500+", icon: "🎯", color: "#ef4444" },
        { metric: "Cost Per Lead", value: "-60%", icon: "💵", color: "#10b981" },
        { metric: "Click-Through Rate", value: "8.5%", icon: "👆", color: "#3b82f6" },
        { metric: "Demo Bookings", value: "120", icon: "📅", color: "#f59e0b" }
      ],
      tags: ["LinkedIn Ads", "Lead Generation", "B2B Marketing"],
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      bgColor: "#f093fb",
      textColor: "#f093fb"
    },
    {
      id: 3,
      category: "Local Business",
      title: "Restaurant Visibility Boost",
      client: "Urban Bite Chain",
      duration: "4 months",
      description: "By optimizing local SEO, we increased foot traffic to a chain of restaurants by 150% in 4 months.",
      challenge: "Poor local search visibility and declining foot traffic post-pandemic",
      solution: "Comprehensive local SEO strategy including Google My Business optimization, local citations, and review management",
      results: [
        { metric: "Foot Traffic", value: "150%", icon: "🚶", color: "#06b6d4" },
        { metric: "Local Rankings", value: "#1-3", icon: "🏆", color: "#f59e0b" },
        { metric: "Online Reviews", value: "4.8★", icon: "⭐", color: "#eab308" },
        { metric: "Revenue Growth", value: "180%", icon: "📊", color: "#10b981" }
      ],
      tags: ["Local SEO", "GMB Optimization", "Reputation Management"],
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      bgColor: "#4facfe",
      textColor: "#4facfe"
    }
  ];

  const clientLogos = [
    "Fashion Forward Co.", "TechFlow Solutions", "Urban Bite Chain", 
    "Digital Dynamics", "Growth Masters", "Success Stories Inc."
  ];

  return (
    <section 
      className="py-4"
      style={{
        // background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Elements */}
      <div 
        style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(102, 126, 234, 0.08) 0%, transparent 70%)',
          borderRadius: '50%'
        }}
      />
      <div 
        style={{
          position: 'absolute',
          bottom: '15%',
          left: '8%',
          width: '150px',
          height: '150px',
          background: 'radial-gradient(circle, rgba(240, 147, 251, 0.08) 0%, transparent 70%)',
          borderRadius: '50%'
        }}
      />

      <div className="container">
        {/* Section Header */}
        <div className="row justify-content-center mb-4">
          <div className="col-lg-8 text-center">
            <div className="mb-3">
              <span 
                className="badge px-3 py-2"
                style={{
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  color: 'white',
                  borderRadius: '50px',
                  border: 'none',
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "1px"
                }}
              >
                🏆 Success Stories
              </span>
            </div>
            <h2 
              className="h1 fw-bold mb-3"
              style={{
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Our Success Stories
            </h2>
            <p 
              className="fs-8"
              style={{ color: '#64748b' }}
            >
              Real results from real clients. See how we've transformed businesses across different industries.
            </p>
          </div>
        </div>

        {/* Case Study Tabs */}
        <div className="row justify-content-center mb-4">
          <div className="col-lg-10">
            <div className="d-flex justify-content-center flex-wrap gap-2">
              {caseStudies.map((study, index) => (
                <button
                  key={study.id}
                  className="btn fw-semibold px-3 py-2"
                  style={{
                    background: activeCase === index ? study.gradient : 'white',
                    color: activeCase === index ? 'white' : study.textColor,
                    border: `2px solid ${study.bgColor}`,
                    borderRadius: '25px',
                    transition: 'all 0.3s ease',
                    transform: activeCase === index ? 'translateY(-2px)' : 'translateY(0)',
                    boxShadow: activeCase === index 
                      ? `0 8px 20px ${study.bgColor}40` 
                      : '0 3px 10px rgba(0,0,0,0.1)',
                    fontSize: '1.5rem'
                  }}
                  onClick={() => setActiveCase(index)}
                >
                  <span className="d-none d-sm-inline">{study.category} - </span>
                  <span className="d-sm-none">{study.category}</span>
                  <span className="d-none d-sm-inline">{study.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active Case Study Display */}
        <div className="row justify-content-center mb-4">
          <div className="col-lg-12">
            <div 
              style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              {/* Case Study Header */}
              <div 
                style={{
                  background: caseStudies[activeCase].gradient,
                  padding: '2rem',
                  color: 'white',
                  position: 'relative'
                }}
              >
                <div className="row align-items-center">
                  <div className="col-lg-8">
                    <div className="mb-2">
                      <span 
                        className="badge px-2 py-1"
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          borderRadius: '15px',
                          fontSize: '1.5rem'
                        }}
                      >
                        {caseStudies[activeCase].category}
                      </span>
                    </div>
                    <h3 className="h3 fw-bold mb-2">
                      {caseStudies[activeCase].title}
                    </h3>
                    <p className="mb-3 opacity-90 fw-bold ">
                      {caseStudies[activeCase].description}
                    </p>
                    <div className="d-flex flex-wrap gap-1">
                      {caseStudies[activeCase].tags.map((tag, idx) => (
                        <span 
                          key={idx}
                          className="badge px-2 py-1"
                          style={{
                            background: 'rgba(255,255,255,0.15)',
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '1.5rem'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="col-lg-4 text-center">
                    <div 
                      className="p-3"
                      style={{
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '15px',
                        backdropFilter: 'blur(10px)'
                      }}
                    >
                      <h5 className="fw-bold mb-1">{caseStudies[activeCase].client}</h5>
                      <p className="mb-0 opacity-90 small">
                        <strong>Duration:</strong> {caseStudies[activeCase].duration}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Results Metrics */}
              <div className="p-3">
                <h5 className="fw-bold mb-3 text-center" style={{ color: '#1e293b' }}>
                  Key Results Achieved
                </h5>
                <div className="row g-3">
                  {caseStudies[activeCase].results.map((result, idx) => (
                    <div key={idx} className="col-lg-3 col-md-6">
                      <div 
                        className="text-center p-3 h-100"
                        style={{
                          background: hoveredMetric === idx 
                            ? `${result.color}10` 
                            : 'rgba(248,250,252,0.8)',
                          borderRadius: '15px',
                          border: `2px solid ${result.color}20`,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          transform: hoveredMetric === idx ? 'translateY(-3px)' : 'translateY(0)',
                          boxShadow: hoveredMetric === idx 
                            ? `0 10px 20px ${result.color}20` 
                            : '0 3px 10px rgba(0,0,0,0.05)'
                        }}
                        onMouseEnter={() => setHoveredMetric(idx)}
                        onMouseLeave={() => setHoveredMetric(null)}
                      >
                        <div className="fs-2 mb-2">
                          {result.icon}
                        </div>
                        <h4 
                          className="fw-bold mb-1"
                          style={{ 
                            color: result.color,
                            fontSize: '1.8rem'
                          }}
                        >
                          {result.value}
                        </h4>
                        <p className="mb-0 fw-semibold small" style={{ color: '#64748b' }}>
                          {result.metric}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Challenge & Solution */}
              <div className="row g-0">
                <div className="col-lg-6">
                  <div 
                    className="p-3 h-100"
                    style={{ 
                      background: 'rgba(239,68,68,0.05)',
                      borderRight: '1px solid rgba(0,0,0,0.1)'
                    }}
                  >
                    <h6 className="fw-bold mb-2" style={{ color: '#ef4444' }}>
                      🎯 The Challenge
                    </h6>
                    <p className="small mb-0" style={{ color: '#64748b', lineHeight: '1.5' }}>
                      {caseStudies[activeCase].challenge}
                    </p>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div 
                    className="p-3 h-100"
                    style={{ background: 'rgba(16,185,129,0.05)' }}
                  >
                    <h6 className="fw-bold mb-2" style={{ color: '#10b981' }}>
                      ⚡ Our Solution
                    </h6>
                    <p className="small mb-0" style={{ color: '#64748b', lineHeight: '1.5' }}>
                      {caseStudies[activeCase].solution}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Client Logos Section */}
        <div className="row justify-content-center mb-4">
          <div className="col-lg-10">
            <div 
              className="text-center p-3"
              style={{
                background: 'white',
                borderRadius: '15px',
                boxShadow: '0 5px 15px rgba(0,0,0,0.08)'
              }}
            >
              <h6 className="fw-bold mb-3" style={{ color: '#1e293b' }}>
                Trusted by Leading Brands
              </h6>
              <div className="row g-2 align-items-center">
                {clientLogos.map((logo, idx) => (
                  <div key={idx} className="col-lg-2 col-md-4 col-6">
                    <div 
                      className="p-2 text-center"
                      style={{
                        background: 'rgba(102,126,234,0.05)',
                        borderRadius: '10px',
                        border: '1px solid rgba(102,126,234,0.1)',
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        color: '#64748b'
                      }}
                    >
                      {logo}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

    
      </div>
    </section>
  );
};

export default SuccessStories;